// /BlogArticle.jsx
import React, { useMemo } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Chip,
  Stack,
  Paper,
  Divider,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LabelIcon from "@mui/icons-material/Label";
import { useSEO, useJsonLD } from "../SEO";
import { POSTS, CATEGORIES } from "./BlogData";

// Theme tokens
const ACCENT = "#F0B90B";
const PAGE_BG = "#0B0E11";
const SURFACE = "rgba(38,50,56,0.85)";
const PANEL_BG = "#263238";
const BORDER = "rgba(255,255,255,0.08)";
const TEXT = "#EAEFF2";
const MUTED = "#B0BEC5";

// Placeholder (still available if needed)
function ImagePlaceholder({ note, alt }) {
  return (
    <Box
      sx={{
        my: 2,
        p: 2,
        borderRadius: 2,
        border: `1px dashed ${ACCENT}`,
        bgcolor: "rgba(240,185,11,0.04)",
      }}
    >
      <Typography variant="caption" sx={{ color: ACCENT, fontWeight: 800 }}>
        IMAGE PLACEHOLDER
      </Typography>
      <Typography variant="body2" sx={{ color: MUTED }}>
        {note}
        {alt ? ` • alt="${alt}"` : ""}
      </Typography>
    </Box>
  );
}

function Callout({ variant = "info", title, text }) {
  const colors = {
    info: { bg: "rgba(240,185,11,0.06)", bd: "rgba(240,185,11,0.4)" },
    warning: { bg: "rgba(255,160,0,0.08)", bd: "rgba(255,160,0,0.5)" },
  };
  const c = colors[variant] || colors.info;
  return (
    <Box
      sx={{
        my: 2,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${c.bd}`,
        bgcolor: c.bg,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ color: ACCENT, fontWeight: 800, mb: 0.5 }}
      >
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: MUTED }}>
        {text}
      </Typography>
    </Box>
  );
}

function KeyList({ title, items }) {
  if (!items?.length) return null;
  return (
    <Box sx={{ my: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ color: ACCENT, fontWeight: 800, mb: 1 }}
      >
        {title}
      </Typography>
      <Stack component="ul" sx={{ pl: 3, m: 0 }}>
        {items.map((it, idx) => (
          <Typography
            component="li"
            key={idx}
            variant="body2"
            sx={{ color: MUTED, mb: 0.75 }}
          >
            {it}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

export default function BlogArticle({
  brandName = "1CryptoX",
  canonicalBase = "https://www.1cryptox.com/blog",
  slug: propSlug,
}) {
  const { slug: routeSlug } = useParams();
  const slug = routeSlug || propSlug || "advanced-order-types";

  const post = useMemo(() => POSTS.find((p) => p.id === slug), [slug]);
  const title = post
    ? `${post.title} • ${brandName}`
    : `Article • ${brandName}`;
  const description = post?.excerpt || "Article";
  const canonical = `${canonicalBase}/${slug}`;

  // SEO
  useSEO({
    title,
    description,
    keywords: [...(post?.tags || []), brandName],
    canonical,
    robots: "index,follow",
    og: {
      type: "article",
      title,
      description,
      url: canonical,
      image: post?.hero || "https://www.1cryptox.com/og/og-article.jpg",
      siteName: brandName,
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      image: post?.hero || "https://www.1cryptox.com/og/og-article.jpg",
    },
  });

  useJsonLD("article", {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post?.title,
    description: post?.excerpt,
    author: post?.author?.name,
    datePublished: post?.date,
    dateModified: post?.date,
    keywords: post?.tags?.join(", "),
    image: post?.hero || "https://www.1cryptox.com/og/og-article.jpg",
    mainEntityOfPage: canonical,
  });

  if (!post) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          bgcolor: PAGE_BG,
          color: TEXT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h6">Article not found.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(60% 60% at 70% 30%, rgba(240,185,11,0.10) 0%, rgba(240,185,11,0.03) 100%)",
        bgcolor: "#0B0E11",
      }}
    >
      <Container sx={{ py: { xs: 5, md: 8 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ color: MUTED, mb: 2 }}>
          <Link
            component={RouterLink}
            to="/blog"
            underline="hover"
            sx={{ color: ACCENT, fontWeight: 700 }}
          >
            Blog
          </Link>
          <Typography sx={{ color: MUTED }}>
            {CATEGORIES.find((c) => c.key === post.category)?.label ||
              "General"}
          </Typography>
          <Typography sx={{ color: TEXT, fontWeight: 800 }}>
            {post.title}
          </Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          {/* Main */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: PANEL_BG,
                border: `1px solid ${BORDER}`,
              }}
            >
              {/* Hero */}
              {post.hero && (
                <Box
                  component="img"
                  src={post.hero}
                  alt={post.title}
                  loading="lazy"
                  sx={{
                    width: "100%",
                    height: { xs: 220, md: 360 },
                    objectFit: "cover",
                    borderRadius: 2,
                    mb: 2,
                    bgcolor: "#0B0E11",
                  }}
                />
              )}

              {/* Meta */}
              <Typography
                variant="h3"
                sx={{ color: TEXT, fontWeight: 900, letterSpacing: 0.2, mb: 1 }}
              >
                {post.title}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ color: MUTED, mb: 2 }}
              >
                <AccessTimeIcon sx={{ color: ACCENT, fontSize: 18 }} />
                <Typography variant="caption">
                  {new Date(post.date).toLocaleDateString()} • {post.readMins}{" "}
                  min read
                </Typography>
                <LabelIcon sx={{ color: ACCENT, fontSize: 18, ml: 2 }} />
                <Typography variant="caption">
                  {CATEGORIES.find((c) => c.key === post.category)?.label ||
                    "General"}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                {(post.tags || []).map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    size="small"
                    sx={{ color: TEXT, border: `1px solid ${BORDER}` }}
                  />
                ))}
              </Stack>

              {/* TL;DR / Takeaways */}
              {post.tldr?.length ? (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: ACCENT, fontWeight: 800, mb: 1 }}
                  >
                    TL;DR
                  </Typography>
                  <Stack component="ul" sx={{ pl: 3, m: 0, mb: 2 }}>
                    {post.tldr.map((it, idx) => (
                      <Typography
                        component="li"
                        key={idx}
                        variant="body2"
                        sx={{ color: MUTED, mb: 0.75 }}
                      >
                        {it}
                      </Typography>
                    ))}
                  </Stack>
                </>
              ) : null}

              {post.takeaways?.length ? (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: ACCENT, fontWeight: 800, mb: 1 }}
                  >
                    Key Takeaways
                  </Typography>
                  <Stack component="ul" sx={{ pl: 3, m: 0, mb: 2 }}>
                    {post.takeaways.map((it, idx) => (
                      <Typography
                        component="li"
                        key={idx}
                        variant="body2"
                        sx={{ color: MUTED, mb: 0.75 }}
                      >
                        {it}
                      </Typography>
                    ))}
                  </Stack>
                </>
              ) : null}

              <Divider sx={{ my: 2, borderColor: BORDER }} />

              {/* Body renderer (supports p, h2, img, callout, disclosure, image placeholder) */}
              {post.body.map((blk, idx) => {
                if (blk.type === "h2") {
                  return (
                    <Typography
                      key={idx}
                      variant="h5"
                      sx={{ color: ACCENT, fontWeight: 800, mt: 2, mb: 1 }}
                    >
                      {blk.text}
                    </Typography>
                  );
                }
                if (blk.type === "p") {
                  return (
                    <Typography
                      key={idx}
                      variant="body1"
                      sx={{ color: "#D6E2E9", mb: 1.5, lineHeight: 1.8 }}
                    >
                      {blk.text}
                    </Typography>
                  );
                }
                if (blk.type === "img") {
                  return (
                    <Box key={idx} sx={{ my: 3 }}>
                      <Box
                        component="img"
                        src={blk.src}
                        alt={blk.alt || post.title}
                        loading="lazy"
                        sx={{
                          width: "100%",
                          borderRadius: 2,
                          objectFit: "cover",
                          maxHeight: 480,
                          bgcolor: "#0B0E11",
                          border: `1px solid ${BORDER}`,
                        }}
                      />
                      {blk.caption && (
                        <Typography
                          variant="caption"
                          sx={{ color: MUTED, display: "block", mt: 0.75 }}
                        >
                          {blk.caption}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                if (blk.type === "image") {
                  // Legacy placeholder
                  return (
                    <ImagePlaceholder key={idx} alt={blk.alt} note={blk.note} />
                  );
                }
                if (blk.type === "callout") {
                  return (
                    <Callout
                      key={idx}
                      variant={blk.variant}
                      title={blk.title}
                      text={blk.text}
                    />
                  );
                }
                if (blk.type === "disclosure") {
                  return (
                    <Card
                      key={idx}
                      elevation={0}
                      sx={{
                        my: 2,
                        borderRadius: 2,
                        bgcolor: "rgba(255,255,255,0.02)",
                        border: `1px dashed ${BORDER}`,
                      }}
                    >
                      <CardContent>
                        <Typography variant="caption" sx={{ color: MUTED }}>
                          {blk.text}
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                }
                return null;
              })}
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${BORDER}`,
                bgcolor: SURFACE,
                backdropFilter: "blur(6px)",
              }}
            >
              <Typography
                variant="overline"
                sx={{ color: ACCENT, letterSpacing: 1, fontWeight: 800 }}
              >
                ABOUT THE AUTHOR
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#D6E2E9", mt: 1, mb: 0.5 }}
              >
                {post.author.name}
              </Typography>
              <Typography variant="caption" sx={{ color: MUTED }}>
                {post.author.role}
              </Typography>
            </Paper>

            <Box sx={{ height: 16 }} />

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${BORDER}`,
                bgcolor: SURFACE,
                backdropFilter: "blur(6px)",
              }}
            >
              <Typography
                variant="overline"
                sx={{ color: ACCENT, letterSpacing: 1, fontWeight: 800 }}
              >
                RELATED
              </Typography>
              {POSTS.filter((p) => p.id !== post.id)
                .slice(0, 3)
                .map((rp) => (
                  <Box key={rp.id} sx={{ my: 1.25 }}>
                    <Link
                      component={RouterLink}
                      to={`/blog/${rp.id}`}
                      underline="hover"
                      sx={{ color: "#EAEFF2", fontWeight: 700 }}
                    >
                      {rp.title}
                    </Link>
                    <Typography
                      variant="caption"
                      sx={{ color: MUTED, display: "block" }}
                    >
                      {new Date(rp.date).toLocaleDateString()} • {rp.readMins}{" "}
                      min
                    </Typography>
                  </Box>
                ))}
            </Paper>

            <Box sx={{ height: 16 }} />

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${BORDER}`,
                bgcolor: SURFACE,
                backdropFilter: "blur(6px)",
              }}
            >
              <Typography
                variant="overline"
                sx={{ color: ACCENT, letterSpacing: 1, fontWeight: 800 }}
              >
                DISCLOSURE
              </Typography>
              <Typography variant="caption" sx={{ color: MUTED }}>
                Content is for educational purposes only and should not be
                construed as financial, legal, or tax advice.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
